import {
    Box, Container, Heading, HStack, Text, Badge, Spinner, Alert, AlertIcon,
    Button, Input, IconButton, SimpleGrid, Stat, StatLabel, StatNumber,
    Flex, Tooltip, useBreakpointValue, VStack, Divider
} from "@chakra-ui/react";
import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FiRefreshCw, FiChevronRight, FiTruck, FiAlertTriangle, FiClock, FiPackage, FiDownload, FiDollarSign } from "react-icons/fi";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
    ResponsiveContainer, Legend, AreaChart, Area, ComposedChart, Line
} from 'recharts';
import { getProcessadorMonitoramentoResumo, getProcessadorMonitoramentoSemanal, exportarProcessadorMonitoramento, exportarRemuneracaoProcessador } from "../services/api";

const DATA_MINIMA = '2025-05-29';

const formatarDataInput = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const maxData = (a, b) => (a > b ? a : b);

const formatarMoeda = (v) => {
    if (v == null) return '0,00';
    return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ChartTooltipDiario = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, boxShadow: '0 2px 8px rgba(0,0,0,.12)' }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>{label}</p>
            <p style={{ fontSize: 11, color: '#16a34a' }}>Despachadas: {d?.despachadas ?? 0}</p>
            <p style={{ fontSize: 11, color: '#dc2626' }}>Falha Conf.: {d?.problemas ?? 0}</p>
            <p style={{ fontSize: 11, color: '#f97316' }}>Atrasadas: {d?.atrasadas ?? 0}</p>
            <p style={{ fontSize: 11, color: '#2563eb' }}>Devoluções: {d?.devolucoes_recebidas ?? 0}</p>
        </div>
    );
};

const ChartTooltipSemanal = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, boxShadow: '0 2px 8px rgba(0,0,0,.12)' }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>{label}</p>
            <p style={{ fontSize: 11, color: '#16a34a' }}>Despacho: R$ {formatarMoeda(d?.valor_despacho)}</p>
            <p style={{ fontSize: 11, color: '#2563eb' }}>Devoluções: R$ {formatarMoeda(d?.valor_devolucoes)}</p>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#0d9488' }}>Total: R$ {formatarMoeda(d?.valor_total)}</p>
        </div>
    );
};

const ChartTooltipTaxa = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, boxShadow: '0 2px 8px rgba(0,0,0,.12)' }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>{label}</p>
            <p style={{ fontSize: 11, color: '#dc2626' }}>Taxa de falhas: {d?.taxa_problemas?.toFixed(1) ?? 0}%</p>
            <p style={{ fontSize: 11, color: '#64748b' }}>{d?.problemas ?? 0} falhas em {d?.total_conferencias ?? 0} conferências</p>
        </div>
    );
};

const ChartTooltipPrazo = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, boxShadow: '0 2px 8px rgba(0,0,0,.12)' }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>{label}</p>
            <p style={{ fontSize: 11, color: '#16a34a' }}>No prazo: {d?.despachadas ?? 0}</p>
            <p style={{ fontSize: 11, color: '#f97316' }}>Atrasadas: {d?.atrasadas ?? 0}</p>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#0d9488' }}>Cumprimento: {d?.taxa_prazo?.toFixed(1) ?? 0}%</p>
        </div>
    );
};

const ChartTooltipAcumulado = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, boxShadow: '0 2px 8px rgba(0,0,0,.12)' }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 4 }}>{label}</p>
            <p style={{ fontSize: 11, color: '#8b5cf6' }}>Acumulado: {d?.acumulado ?? 0} etiquetas</p>
            <p style={{ fontSize: 11, color: '#16a34a' }}>No dia: {d?.despachadas ?? 0}</p>
        </div>
    );
};

export default function ProcessadorMonitoramento() {
    const navigate = useNavigate();
    const hoje = formatarDataInput(new Date());
    const hojeFormatado = hoje.split('-').reverse().join('/');
    const hojeDate = new Date();
    const domingoDaSemana = formatarDataInput(new Date(hojeDate.getTime() - hojeDate.getDay() * 86400000));
    const dataFimInicial = maxData(hoje, DATA_MINIMA);
    const dataInicioInicial = maxData(domingoDaSemana, DATA_MINIMA);
    const isMobile = useBreakpointValue({ base: true, md: false });

    const [dataInicio, setDataInicio] = useState(dataInicioInicial);
    const [dataFim, setDataFim] = useState(dataFimInicial);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportingRem, setExportingRem] = useState(false);
    const [resumo, setResumo] = useState(null);
    const [totaisHoje, setTotaisHoje] = useState(null);
    const [erro, setErro] = useState(null);
    const [pagina, setPagina] = useState(1);
    const [semanal, setSemanal] = useState(null);
    const [calendarioDias, setCalendarioDias] = useState(null);
    const [filtroAtivo, setFiltroAtivo] = useState('semana');
    const porPagina = 15;

    const aplicarFiltroRapido = (tipo) => {
        const h = new Date();
        let ini, fim;
        const diaSemana = h.getDay();
        switch (tipo) {
            case 'hoje':
                ini = fim = formatarDataInput(h);
                break;
            case 'semana':
                ini = formatarDataInput(new Date(h.getTime() - diaSemana * 86400000));
                fim = formatarDataInput(h);
                break;
            case 'semana_passada': {
                const domPassado = new Date(h.getTime() - (diaSemana + 7) * 86400000);
                ini = formatarDataInput(domPassado);
                fim = formatarDataInput(new Date(domPassado.getTime() + 6 * 86400000));
                break;
            }
            case '7dias':
                ini = formatarDataInput(new Date(h.getTime() - 6 * 86400000));
                fim = formatarDataInput(h);
                break;
            case '30dias':
                ini = formatarDataInput(new Date(h.getTime() - 29 * 86400000));
                fim = formatarDataInput(h);
                break;
            case 'mes': {
                ini = formatarDataInput(new Date(h.getFullYear(), h.getMonth(), 1));
                fim = formatarDataInput(h);
                break;
            }
            default:
                return;
        }
        setDataInicio(maxData(ini, DATA_MINIMA));
        setDataFim(maxData(fim, DATA_MINIMA));
        setFiltroAtivo(tipo);
    };

    // ---- Períodos pagáveis da remuneração ----
    //
    // A remuneração é semanal (dom–sáb) e as faixas são regressivas. Duas coisas
    // impedem um período de ser pagável:
    //   - semana CORTADA: aparece inteira também no período vizinho, e somar os
    //     dois pagaria a mesma semana duas vezes;
    //   - semana EM CURSO: o valor ainda vai subir com o volume que falta.
    // Os atalhos abaixo sempre produzem semanas civis inteiras e já fechadas.

    const dataParaTexto = (d) => maxData(formatarDataInput(d), DATA_MINIMA);

    /** Sábado da última semana que JÁ FECHOU (a que termina hoje ainda está correndo). */
    const ultimoSabadoFechado = () => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() - 1);
        while (d.getDay() !== 6) d.setDate(d.getDate() - 1);
        return d;
    };

    /**
     * Liga/desliga um atalho da remuneracao. Clicar no que ja esta ativo volta
     * ao periodo padrao, igual ao "Limpar" do topo — que fica fora de vista
     * quando se esta olhando este bloco.
     */
    const alternarAtalho = (chave, aplicar) => {
        if (filtroAtivo === chave) {
            aplicarFiltroRapido('semana');
            return;
        }
        aplicar();
    };

    const aplicarSemanas = (sabadoFinal, quantidade, chave) => {
        const fim = new Date(sabadoFinal);
        const ini = new Date(sabadoFinal);
        ini.setDate(ini.getDate() - (7 * quantidade - 1));
        setDataInicio(dataParaTexto(ini));
        setDataFim(dataParaTexto(fim));
        setFiltroAtivo(chave);
    };

    /** Semanas que FECHAM dentro do mês corrente, sem incluir a que ainda corre. */
    const semanasFechadasDoMes = () => {
        const h = new Date();
        const primeiroSab = new Date(h.getFullYear(), h.getMonth(), 1);
        while (primeiroSab.getDay() !== 6) primeiroSab.setDate(primeiroSab.getDate() + 1);
        const ultimoSab = ultimoSabadoFechado();
        if (primeiroSab > ultimoSab) return null;
        return { primeiroSab, ultimoSab };
    };

    // O calendário é uma visão geral do MÊS: não pode depender do recorte de
    // datas, senão os dias fora do filtro aparecem zerados (e não estão), e a
    // escala de cor muda de significado a cada filtro. Busca própria, do mês
    // inteiro da data final.
    const carregarCalendario = useCallback(async () => {
        const [ano, mes] = dataFim.split('-').map(Number);
        const ultimoDiaMes = new Date(ano, mes, 0).getDate();
        const mm = String(mes).padStart(2, '0');
        const res = await getProcessadorMonitoramentoResumo(
            maxData(`${ano}-${mm}-01`, DATA_MINIMA),
            `${ano}-${mm}-${String(ultimoDiaMes).padStart(2, '0')}`
        );
        setCalendarioDias(res?.error ? [] : (res?.dias || []));
    }, [dataFim]);

    const carregar = useCallback(async () => {
        setLoading(true);
        setErro(null);
        const [res, resSemanal] = await Promise.all([
            getProcessadorMonitoramentoResumo(dataInicio, dataFim),
            getProcessadorMonitoramentoSemanal(dataInicio, dataFim),
        ]);
        if (res?.error) {
            setErro(res.error);
            setResumo(null);
        } else {
            setResumo(res);
            setPagina(1);
        }
        if (!resSemanal?.error) {
            setSemanal(resSemanal);
        }
        setLoading(false);
    }, [dataInicio, dataFim]);

    const carregarHoje = useCallback(async () => {
        if (hoje < DATA_MINIMA) return;
        const res = await getProcessadorMonitoramentoResumo(hoje, hoje);
        if (!res?.error) {
            setTotaisHoje(res.totais);
        }
    }, [hoje]);

    const handleExportar = async () => {
        setExporting(true);
        const res = await exportarProcessadorMonitoramento(dataInicio, dataFim);
        if (res?.error) {
            setErro('Erro ao exportar: ' + res.error);
        }
        setExporting(false);
    };

    useEffect(() => {
        carregar();
        carregarHoje();
        carregarCalendario();
    }, [dataInicio, dataFim]);

    const calendarioData = useMemo(() => {
        if (!calendarioDias) return null;
        const mapDia = {};
        calendarioDias.forEach(d => { mapDia[d.data] = d; });

        // O mês exibido acompanha a data final do filtro. Antes era sempre o mês
        // de hoje: filtrar julho mostrava um calendário de setembro, em branco.
        const [anoFiltro, mesFiltro] = dataFim.split('-').map(Number);
        const ano = anoFiltro;
        const mes = mesFiltro - 1;
        const primeiroDia = new Date(ano, mes, 1);
        const ultimoDia = new Date(ano, mes + 1, 0);
        const diasNoMes = ultimoDia.getDate();
        const offsetInicio = primeiroDia.getDay();

        const nomesMes = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
        const cells = [];
        for (let i = 0; i < offsetInicio; i++) cells.push(null);
        for (let d = 1; d <= diasNoMes; d++) {
            const dataStr = `${ano}-${String(mes + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
            const info = mapDia[dataStr];
            const total = info ? (info.despachadas + info.devolucoes_recebidas + info.atrasadas) : 0;
            cells.push({ dia: d, data: dataStr, total, info, isHoje: dataStr === hoje });
        }

        let maxVal = 0;
        cells.forEach(c => { if (c && c.total > maxVal) maxVal = c.total; });

        return { cells, nomeMes: `${nomesMes[mes]} ${ano}`, maxVal };
    }, [calendarioDias, dataFim, hoje]);

    const chartsDerivedData = useMemo(() => {
        if (!resumo?.dias?.length) return null;
        const diasAsc = [...resumo.dias].reverse();

        let acumulado = 0;
        const acumuladoData = diasAsc.map(d => {
            acumulado += d.despachadas;
            return { data_formatada: d.data_formatada, despachadas: d.despachadas, acumulado };
        });

        const taxaProblemasData = diasAsc.map(d => {
            const total = d.despachadas + d.problemas;
            return {
                data_formatada: d.data_formatada,
                taxa_problemas: total > 0 ? (d.problemas / total) * 100 : 0,
                problemas: d.problemas,
                total_conferencias: total,
            };
        });

        const taxaPrazoData = diasAsc.map(d => {
            const total = d.despachadas + d.atrasadas;
            return {
                data_formatada: d.data_formatada,
                taxa_prazo: total > 0 ? (d.despachadas / total) * 100 : 0,
                despachadas: d.despachadas,
                atrasadas: d.atrasadas,
            };
        });

        return { acumuladoData, taxaProblemasData, taxaPrazoData };
    }, [resumo]);

    const abrirDetalhe = (data) => {
        navigate(`/processador/monitoramento/${data}`);
    };

    return (
        <Box minH="100vh" bg="#f0f0f0" py={{ base: 3, md: 6 }} px={{ base: 2, md: 4 }}>
            <Container maxW="container.xl" px={{ base: 0, md: 4 }}>
                <Flex
                    direction={{ base: 'column', md: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ base: 'stretch', md: 'center' }}
                    mb={4}
                    gap={2}
                >
                    <Heading size={{ base: 'sm', md: 'lg' }} color="#01695E">
                        Monitoramento Processamento de Pedidos
                    </Heading>
                    <HStack spacing={2} justify={{ base: 'space-between', md: 'flex-end' }}>
                        <Button
                            leftIcon={<FiRefreshCw />}
                            size={{ base: 'xs', md: 'sm' }}
                            colorScheme="teal"
                            variant="outline"
                            onClick={carregar}
                            isLoading={loading}
                            flex={{ base: 1, md: 'none' }}
                        >
                            Atualizar
                        </Button>
                        <Button
                            leftIcon={<FiDownload />}
                            size={{ base: 'xs', md: 'sm' }}
                            colorScheme="orange"
                            bg="#F68633"
                            color="white"
                            _hover={{ bg: '#e67722' }}
                            onClick={handleExportar}
                            isLoading={exporting}
                            flex={{ base: 1, md: 'none' }}
                        >
                            {isMobile ? 'Exportar' : 'Exportar Excel'}
                        </Button>
                    </HStack>
                </Flex>

                <Box bg="white" p={3} rounded="md" shadow="sm" mb={3}>
                    <Flex direction={{ base: 'column', md: 'row' }} gap={3} align={{ base: 'stretch', md: 'flex-end' }}>
                        <Box flex={{ base: 'none', md: 1 }} maxW={{ md: '200px' }}>
                            <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>Data inicial</Text>
                            <Input
                                type="date"
                                size="sm"
                                value={dataInicio}
                                min={DATA_MINIMA}
                                max={dataFim}
                                onChange={(e) => { setDataInicio(e.target.value); setFiltroAtivo(null); }}
                            />
                        </Box>
                        <Box flex={{ base: 'none', md: 1 }} maxW={{ md: '200px' }}>
                            <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>Data final</Text>
                            <Input
                                type="date"
                                size="sm"
                                value={dataFim}
                                min={maxData(dataInicio, DATA_MINIMA)}
                                max={maxData(hoje, DATA_MINIMA)}
                                onChange={(e) => { setDataFim(e.target.value); setFiltroAtivo(null); }}
                            />
                        </Box>
                    </Flex>
                    <Flex mt={2} gap={1} flexWrap="wrap" align="center">
                        {[
                            { key: 'hoje', label: 'Hoje' },
                            { key: 'semana', label: 'Esta Semana' },
                            { key: 'semana_passada', label: 'Sem. Passada' },
                            { key: '7dias', label: '7 Dias' },
                            { key: '30dias', label: '30 Dias' },
                            { key: 'mes', label: 'Este Mês' },
                        ].map(f => (
                            <Button
                                key={f.key}
                                size="xs"
                                variant={filtroAtivo === f.key ? 'solid' : 'outline'}
                                colorScheme={filtroAtivo === f.key ? 'teal' : 'gray'}
                                onClick={() => aplicarFiltroRapido(f.key)}
                                fontWeight={filtroAtivo === f.key ? 'bold' : 'normal'}
                            >
                                {f.label}
                            </Button>
                        ))}
                        {filtroAtivo && (
                            <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => {
                                    setFiltroAtivo(null);
                                    aplicarFiltroRapido('semana');
                                }}
                                fontWeight="normal"
                            >
                                Limpar
                            </Button>
                        )}
                    </Flex>
                </Box>

                {erro && (
                    <Alert status="error" borderRadius="md" mb={4} fontSize="sm">
                        <AlertIcon />
                        {erro}
                    </Alert>
                )}

                {loading && !resumo && (
                    <Flex justify="center" align="center" h="300px">
                        <Spinner size="xl" color="#F68633" thickness="4px" />
                    </Flex>
                )}

                {totaisHoje && (
                    <Box mb={3}>
                        <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1} textTransform="uppercase">
                            Métricas de hoje — {hojeFormatado}
                        </Text>
                        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 2, md: 4 }}>
                            <CardTotal label="Despachadas" valor={totaisHoje.despachadas} cor="green" icon={FiTruck} />
                            <CardTotal label="Falha de Conferência" valor={totaisHoje.problemas} cor="red" icon={FiAlertTriangle} />
                            <CardTotal label="Atrasadas" valor={totaisHoje.atrasadas} cor="orange" icon={FiClock} />
                            <CardTotal label="Devoluções" valor={totaisHoje.devolucoes_recebidas} cor="blue" icon={FiPackage} />
                        </SimpleGrid>
                    </Box>
                )}

                {resumo && (() => {
                    const totalItens = resumo.dias.length;
                    const totalPaginas = Math.max(1, Math.ceil(totalItens / porPagina));
                    const paginaAtual = Math.min(pagina, totalPaginas);
                    const inicio = (paginaAtual - 1) * porPagina;
                    const diasPagina = resumo.dias.slice(inicio, inicio + porPagina);
                    return (
                    <>
                        <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1} textTransform="uppercase">
                            Histórico Diário
                        </Text>

                        {isMobile ? (
                            <VStack spacing={2} align="stretch">
                                {totalItens === 0 && (
                                    <Box bg="white" p={6} rounded="md" textAlign="center" color="gray.500">
                                        <Text fontSize="sm">Nenhum dado no período.</Text>
                                    </Box>
                                )}
                                {diasPagina.map((l) => (
                                    <Box
                                        key={l.data}
                                        bg="white"
                                        p={3}
                                        rounded="md"
                                        shadow="sm"
                                        borderWidth="1px"
                                        borderColor="gray.200"
                                        onClick={() => abrirDetalhe(l.data)}
                                        cursor="pointer"
                                        _active={{ bg: 'gray.50' }}
                                    >
                                        <Flex justify="space-between" align="center" mb={2}>
                                            <Text fontWeight="bold" color="gray.800">{l.data_formatada}</Text>
                                            <Box as={FiChevronRight} color="gray.400" />
                                        </Flex>
                                        <Divider mb={2} />
                                        <SimpleGrid columns={2} spacing={2}>
                                            <LinhaMobile label="Despachadas" valor={l.despachadas} cor="green" />
                                            <LinhaMobile label="Falha Conferência" valor={l.problemas} cor="red" />
                                            <LinhaMobile label="Atrasadas" valor={l.atrasadas} cor="orange" />
                                            <LinhaMobile label="Devoluções" valor={l.devolucoes_recebidas} cor="blue" />
                                        </SimpleGrid>
                                    </Box>
                                ))}
                            </VStack>
                        ) : (
                            <Box bg="white" rounded="md" shadow="sm" overflowX="auto">
                                <Box as="table" w="100%" fontSize="sm">
                                    <Box as="thead" bg="gray.100">
                                        <Box as="tr">
                                            <Box as="th" textAlign="left" px={3} py={2}>Data</Box>
                                            <Box as="th" textAlign="center" px={3} py={2}>Despachadas</Box>
                                            <Box as="th" textAlign="center" px={3} py={2}>Falha Conferência</Box>
                                            <Box as="th" textAlign="center" px={3} py={2}>Atrasadas</Box>
                                            <Box as="th" textAlign="center" px={3} py={2}>Devoluções</Box>
                                            <Box as="th" textAlign="center" px={3} py={2}>Ação</Box>
                                        </Box>
                                    </Box>
                                    <Box as="tbody">
                                        {totalItens === 0 && (
                                            <Box as="tr">
                                                <Box as="td" colSpan={6} textAlign="center" color="gray.500" py={6}>
                                                    Nenhum dado encontrado no período selecionado.
                                                </Box>
                                            </Box>
                                        )}
                                        {diasPagina.map((linha) => (
                                            <Box
                                                as="tr"
                                                key={linha.data}
                                                _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                                                borderTopWidth="1px"
                                                borderColor="gray.100"
                                                onClick={() => abrirDetalhe(linha.data)}
                                            >
                                                <Box as="td" px={3} py={2} fontWeight="bold">{linha.data_formatada}</Box>
                                                <Box as="td" px={3} py={2} textAlign="center">
                                                    <Badge colorScheme="green" variant={linha.despachadas > 0 ? 'solid' : 'subtle'}>{linha.despachadas}</Badge>
                                                </Box>
                                                <Box as="td" px={3} py={2} textAlign="center">
                                                    <Badge colorScheme="red" variant={linha.problemas > 0 ? 'solid' : 'subtle'}>{linha.problemas}</Badge>
                                                </Box>
                                                <Box as="td" px={3} py={2} textAlign="center">
                                                    <Badge colorScheme="orange" variant={linha.atrasadas > 0 ? 'solid' : 'subtle'}>{linha.atrasadas}</Badge>
                                                </Box>
                                                <Box as="td" px={3} py={2} textAlign="center">
                                                    <Badge colorScheme="blue" variant={linha.devolucoes_recebidas > 0 ? 'solid' : 'subtle'}>{linha.devolucoes_recebidas}</Badge>
                                                </Box>
                                                <Box as="td" px={3} py={2} textAlign="center">
                                                    <Tooltip label="Ver detalhes do dia">
                                                        <IconButton
                                                            icon={<FiChevronRight />}
                                                            size="xs"
                                                            colorScheme="teal"
                                                            aria-label="Detalhes"
                                                            onClick={(e) => { e.stopPropagation(); abrirDetalhe(linha.data); }}
                                                        />
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                        ))}
                                        {totalItens > 0 && (
                                            <Box as="tr" bg="gray.50" borderTopWidth="2px" borderColor="gray.300">
                                                <Box as="td" px={3} py={2} fontWeight="bold" color="gray.700">TOTAL</Box>
                                                <Box as="td" px={3} py={2} textAlign="center" fontWeight="bold">{resumo.totais.despachadas}</Box>
                                                <Box as="td" px={3} py={2} textAlign="center" fontWeight="bold">{resumo.totais.problemas}</Box>
                                                <Box as="td" px={3} py={2} textAlign="center" fontWeight="bold">{resumo.totais.atrasadas}</Box>
                                                <Box as="td" px={3} py={2} textAlign="center" fontWeight="bold">{resumo.totais.devolucoes_recebidas}</Box>
                                                <Box as="td" px={3} py={2}></Box>
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {totalItens > porPagina && (
                            <Flex justify="space-between" align="center" mt={3} px={1}>
                                <Text fontSize="xs" color="gray.600">
                                    Mostrando {inicio + 1}-{Math.min(inicio + porPagina, totalItens)} de {totalItens}
                                </Text>
                                <HStack spacing={2}>
                                    <Button
                                        size="xs"
                                        variant="outline"
                                        onClick={() => setPagina(paginaAtual - 1)}
                                        isDisabled={paginaAtual <= 1}
                                    >
                                        Anterior
                                    </Button>
                                    <Text fontSize="xs" color="gray.700" fontWeight="bold">
                                        {paginaAtual} / {totalPaginas}
                                    </Text>
                                    <Button
                                        size="xs"
                                        variant="outline"
                                        onClick={() => setPagina(paginaAtual + 1)}
                                        isDisabled={paginaAtual >= totalPaginas}
                                    >
                                        Próxima
                                    </Button>
                                </HStack>
                            </Flex>
                        )}
                    </>
                    );
                })()}

                {semanal && semanal.semanas && semanal.semanas.length > 0 && (
                    <Box mt={6}>
                        <Flex justify="space-between" align="center" mb={1} wrap="wrap" gap={2}>
                            <HStack spacing={2}>
                                <Box as={FiDollarSign} color="teal.500" boxSize={4} />
                                <Text fontSize="xs" fontWeight="bold" color="gray.600" textTransform="uppercase">
                                    Remuneração Semanal (Dom-Sáb)
                                </Text>
                            </HStack>

                            {/* Atalhos que sempre caem em semanas inteiras e ja fechadas —
                                os unicos periodos que dao um total pagavel. */}
                            <HStack spacing={1} wrap="wrap">
                                <Tooltip label={filtroAtivo === 'sem_1' ? 'Clique de novo para desmarcar' : 'A última semana que já fechou (dom–sáb)'}>
                                    <Button size="xs" colorScheme="teal"
                                        variant={filtroAtivo === 'sem_1' ? 'solid' : 'outline'}
                                        onClick={() => alternarAtalho('sem_1', () => aplicarSemanas(ultimoSabadoFechado(), 1, 'sem_1'))}>
                                        Última semana fechada
                                    </Button>
                                </Tooltip>
                                <Tooltip label={filtroAtivo === 'sem_4' ? 'Clique de novo para desmarcar' : 'As 4 últimas semanas já fechadas'}>
                                    <Button size="xs" colorScheme="teal"
                                        variant={filtroAtivo === 'sem_4' ? 'solid' : 'outline'}
                                        onClick={() => alternarAtalho('sem_4', () => aplicarSemanas(ultimoSabadoFechado(), 4, 'sem_4'))}>
                                        Últimas 4 semanas
                                    </Button>
                                </Tooltip>
                                <Tooltip label={
                                    !semanasFechadasDoMes() ? 'Nenhuma semana fechou neste mês ainda'
                                    : filtroAtivo === 'sem_mes' ? 'Clique de novo para desmarcar'
                                    : 'As semanas que fecharam dentro deste mês'
                                }>
                                    <Box>
                                        <Button size="xs" colorScheme="teal"
                                            variant={filtroAtivo === 'sem_mes' ? 'solid' : 'outline'}
                                            isDisabled={!semanasFechadasDoMes()}
                                            onClick={() => alternarAtalho('sem_mes', () => {
                                                const r = semanasFechadasDoMes();
                                                if (!r) return;
                                                const ini = new Date(r.primeiroSab);
                                                ini.setDate(ini.getDate() - 6);
                                                setDataInicio(dataParaTexto(ini));
                                                setDataFim(dataParaTexto(r.ultimoSab));
                                                setFiltroAtivo('sem_mes');
                                            })}>
                                            Semanas fechadas do mês
                                        </Button>
                                    </Box>
                                </Tooltip>
                            </HStack>
                            <Button
                                leftIcon={<FiDownload />}
                                size="xs"
                                colorScheme="teal"
                                variant="outline"
                                isLoading={exportingRem}
                                onClick={async () => {
                                    setExportingRem(true);
                                    await exportarRemuneracaoProcessador(dataInicio, dataFim);
                                    setExportingRem(false);
                                }}
                            >
                                {isMobile ? 'Excel' : 'Exportar Remuneração'}
                            </Button>
                        </Flex>

                        {isMobile ? (
                            <VStack spacing={2} align="stretch">
                                {semanal.semanas.map((s) => (
                                    <Box key={s.semana_inicio} bg="white" p={3} rounded="md" shadow="sm" borderWidth="1px" borderColor="gray.200">
                                        <Flex align="center" gap={2} mb={2} wrap="wrap">
                                            <Text fontWeight="bold" color="gray.800" fontSize="sm">{s.semana_label}</Text>
                                            {s.em_curso && (
                                                <Badge colorScheme="yellow" fontSize="9px" borderRadius="full">em curso</Badge>
                                            )}
                                        </Flex>
                                        <Divider mb={2} />
                                        <SimpleGrid columns={2} spacing={3}>
                                            <Box>
                                                <Text fontSize="10px" color="gray.500" fontWeight="medium">Despachadas</Text>
                                                <HStack spacing={1} align="baseline">
                                                    <Text fontSize="lg" fontWeight="bold" color="green.600">{s.remuneracao.despachadas}</Text>
                                                    <Text fontSize="xs" color="green.500">R$ {formatarMoeda(s.remuneracao.valor_despacho)}</Text>
                                                </HStack>
                                            </Box>
                                            <Box>
                                                <Text fontSize="10px" color="gray.500" fontWeight="medium">Devoluções</Text>
                                                <HStack spacing={1} align="baseline">
                                                    <Text fontSize="lg" fontWeight="bold" color="blue.600">{s.remuneracao.devolucoes}</Text>
                                                    <Text fontSize="xs" color="blue.500">R$ {formatarMoeda(s.remuneracao.valor_devolucoes)}</Text>
                                                </HStack>
                                            </Box>
                                        </SimpleGrid>
                                        <Divider my={2} />
                                        <Flex justify="space-between" align="center">
                                            <Text fontSize="xs" fontWeight="bold" color="gray.700">Total da semana:</Text>
                                            <Badge colorScheme="teal" fontSize="md" px={2} py={1}>R$ {formatarMoeda(s.remuneracao.valor_total)}</Badge>
                                        </Flex>
                                        {s.parcial && (
                                            <Flex justify="space-between" align="center" mt={2} pt={2} borderTopWidth="1px" borderColor="gray.100">
                                                <Text fontSize="10px" color="gray.500">
                                                    Dentro do filtro ({s.dias_no_periodo} de 7 dias)
                                                </Text>
                                                <Text fontSize="xs" color="gray.600" fontWeight="medium">
                                                    R$ {formatarMoeda(s.remuneracao_periodo?.valor_total)}
                                                </Text>
                                            </Flex>
                                        )}
                                    </Box>
                                ))}
                                {semanal.semanas.length > 0 && (
                                    <Box bg="teal.50" p={3} rounded="md" borderWidth="2px" borderColor="teal.300">
                                        <Flex justify="space-between" align="center">
                                            <Text fontSize="sm" fontWeight="bold" color="teal.800">
                                                {(semanal.tem_semana_parcial || semanal.tem_semana_em_curso) ? 'TOTAL DAS SEMANAS LISTADAS' : 'TOTAL A PAGAR'}
                                            </Text>
                                            <Text fontSize="lg" fontWeight="bold" color="teal.800">R$ {formatarMoeda(semanal.total_remuneracao)}</Text>
                                        </Flex>
                                    </Box>
                                )}
                            </VStack>
                        ) : (
                            <Box bg="white" rounded="md" shadow="sm" overflowX="auto">
                                <Box as="table" w="100%" fontSize="sm">
                                    <Box as="thead" bg="gray.100">
                                        <Box as="tr">
                                            <Box as="th" textAlign="left" px={3} py={2}>Semana</Box>
                                            <Box as="th" textAlign="center" px={3} py={2}>Despachadas</Box>
                                            <Box as="th" textAlign="center" px={3} py={2}>Devoluções</Box>
                                            <Box as="th" textAlign="right" px={3} py={2}>Valor Despacho</Box>
                                            <Box as="th" textAlign="right" px={3} py={2}>Valor Devoluções</Box>
                                            <Box as="th" textAlign="right" px={3} py={2}>Total</Box>
                                        </Box>
                                    </Box>
                                    <Box as="tbody">
                                        {semanal.semanas.map((s) => (
                                            <Box as="tr" key={s.semana_inicio} borderTopWidth="1px" borderColor="gray.100" _hover={{ bg: 'gray.50' }}>
                                                <Box as="td" px={3} py={2}>
                                                    <Flex align="center" gap={2} wrap="wrap">
                                                        <Text fontSize="sm" fontWeight="bold">{s.semana_label}</Text>
                                                        {s.em_curso && (
                                                            <Tooltip label="A semana ainda não fechou. Como as faixas são regressivas, o valor sobe conforme o volume da semana cresce — só estabiliza no sábado. Não pague por este número.">
                                                                <Badge colorScheme="yellow" fontSize="9px" borderRadius="full" cursor="help">em curso</Badge>
                                                            </Tooltip>
                                                        )}
                                                    </Flex>
                                                    {s.parcial && (
                                                        <Text fontSize="10px" color="gray.500" mt={0.5}>
                                                            no filtro: {s.dias_no_periodo} de 7 dias · R$ {formatarMoeda(s.remuneracao_periodo?.valor_total)}
                                                        </Text>
                                                    )}
                                                </Box>
                                                <Box as="td" px={3} py={2} textAlign="center">
                                                    <Badge colorScheme="green" variant={s.remuneracao.despachadas > 0 ? 'solid' : 'subtle'}>{s.remuneracao.despachadas}</Badge>
                                                </Box>
                                                <Box as="td" px={3} py={2} textAlign="center">
                                                    <Badge colorScheme="blue" variant={s.remuneracao.devolucoes > 0 ? 'solid' : 'subtle'}>{s.remuneracao.devolucoes}</Badge>
                                                </Box>
                                                <Box as="td" px={3} py={2} textAlign="right" color="green.700">R$ {formatarMoeda(s.remuneracao.valor_despacho)}</Box>
                                                <Box as="td" px={3} py={2} textAlign="right" color="blue.700">R$ {formatarMoeda(s.remuneracao.valor_devolucoes)}</Box>
                                                <Box as="td" px={3} py={2} textAlign="right" fontWeight="bold" color="teal.700">R$ {formatarMoeda(s.remuneracao.valor_total)}</Box>
                                            </Box>
                                        ))}
                                        <Box as="tr" bg="teal.50" borderTopWidth="2px" borderColor="teal.300">
                                            <Box as="td" px={3} py={2} fontWeight="bold" color="teal.800">
                                                {(semanal.tem_semana_parcial || semanal.tem_semana_em_curso) ? 'TOTAL DAS SEMANAS LISTADAS' : 'TOTAL A PAGAR'}
                                            </Box>
                                            <Box as="td" px={3} py={2} textAlign="center" fontWeight="bold">
                                                {semanal.semanas.reduce((sum, s) => sum + s.remuneracao.despachadas, 0)}
                                            </Box>
                                            <Box as="td" px={3} py={2} textAlign="center" fontWeight="bold">
                                                {semanal.semanas.reduce((sum, s) => sum + s.remuneracao.devolucoes, 0)}
                                            </Box>
                                            <Box as="td" px={3} py={2} textAlign="right" fontWeight="bold" color="green.700">
                                                R$ {formatarMoeda(semanal.semanas.reduce((sum, s) => sum + s.remuneracao.valor_despacho, 0))}
                                            </Box>
                                            <Box as="td" px={3} py={2} textAlign="right" fontWeight="bold" color="blue.700">
                                                R$ {formatarMoeda(semanal.semanas.reduce((sum, s) => sum + s.remuneracao.valor_devolucoes, 0))}
                                            </Box>
                                            <Box as="td" px={3} py={2} textAlign="right" fontWeight="bold" fontSize="md" color="teal.800">
                                                R$ {formatarMoeda(semanal.total_remuneracao)}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        )}

                        {calendarioData && (
                            <Box bg="white" p={4} rounded="md" shadow="sm" mt={4} borderWidth="1px" borderColor="gray.100">
                                <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={3} textTransform="uppercase">
                                    {calendarioData.nomeMes}
                                </Text>
                                <SimpleGrid columns={7} spacing={1}>
                                    {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => (
                                        <Box key={d} textAlign="center">
                                            <Text fontSize="9px" fontWeight="bold" color="gray.400">{d}</Text>
                                        </Box>
                                    ))}
                                    {calendarioData.cells.map((cell, i) => {
                                        if (!cell) return <Box key={`empty-${i}`} />;
                                        const intensidade = calendarioData.maxVal > 0
                                            ? Math.min(cell.total / calendarioData.maxVal, 1)
                                            : 0;
                                        let bg = 'gray.50';
                                        if (cell.total > 0) {
                                            if (intensidade <= 0.25) bg = 'green.100';
                                            else if (intensidade <= 0.5) bg = 'green.200';
                                            else if (intensidade <= 0.75) bg = 'green.400';
                                            else bg = 'green.600';
                                        }
                                        const futuro = cell.data > hoje;
                                        return (
                                            <Tooltip
                                                key={cell.data}
                                                label={cell.info
                                                    ? `${cell.dia}/${calendarioData.nomeMes.split(' ')[0]}: ${cell.info.despachadas} desp. | ${cell.info.problemas} prob. | ${cell.info.atrasadas} atras. | ${cell.info.devolucoes_recebidas} dev.`
                                                    : `${cell.dia} - Sem dados`
                                                }
                                                fontSize="xs"
                                                hasArrow
                                            >
                                                <Box
                                                    textAlign="center"
                                                    py={{ base: 1, md: 2 }}
                                                    rounded="md"
                                                    bg={futuro ? 'gray.25' : bg}
                                                    opacity={futuro ? 0.4 : 1}
                                                    borderWidth={cell.isHoje ? '2px' : '1px'}
                                                    borderColor={cell.isHoje ? 'teal.500' : 'transparent'}
                                                    cursor={!futuro && cell.info ? 'pointer' : 'default'}
                                                    onClick={() => !futuro && cell.info && abrirDetalhe(cell.data)}
                                                    _hover={!futuro && cell.info ? { shadow: 'md', transform: 'scale(1.05)' } : {}}
                                                    transition="all 0.15s"
                                                >
                                                    <Text
                                                        fontSize={{ base: '10px', md: '12px' }}
                                                        fontWeight={cell.isHoje ? 'bold' : 'medium'}
                                                        color={cell.total > 0 && intensidade > 0.5 ? 'white' : 'gray.700'}
                                                    >
                                                        {cell.dia}
                                                    </Text>
                                                    {!isMobile && cell.total > 0 && (
                                                        <Text
                                                            fontSize="9px"
                                                            fontWeight="bold"
                                                            color={intensidade > 0.5 ? 'whiteAlpha.900' : 'green.700'}
                                                        >
                                                            {cell.total}
                                                        </Text>
                                                    )}
                                                </Box>
                                            </Tooltip>
                                        );
                                    })}
                                </SimpleGrid>
                                <Flex mt={2} gap={1} align="center" justify="flex-end">
                                    <Text fontSize="9px" color="gray.400">Menos</Text>
                                    {['gray.100', 'green.100', 'green.200', 'green.400', 'green.600'].map((c, i) => (
                                        <Box key={i} w="12px" h="12px" bg={c} rounded="sm" borderWidth="1px" borderColor="gray.200" />
                                    ))}
                                    <Text fontSize="9px" color="gray.400">Mais</Text>
                                </Flex>
                            </Box>
                        )}

                        {resumo && resumo.dias.length > 0 && (
                            <>
                                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4} mt={4}>
                                    <Box bg="white" p={4} rounded="md" shadow="sm" borderWidth="1px" borderColor="gray.100">
                                        <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={3} textTransform="uppercase">
                                            Atividade Diária
                                        </Text>
                                        <Box h={{ base: '220px', md: '260px' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={[...resumo.dias].reverse()} barCategoryGap="20%">
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                                    <XAxis dataKey="data_formatada" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                                                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} width={30} />
                                                    <RTooltip content={<ChartTooltipDiario />} />
                                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                                    <Bar dataKey="despachadas" name="Despachadas" fill="#16a34a" radius={[3,3,0,0]} />
                                                    <Bar dataKey="problemas" name="Falha Conf." fill="#dc2626" radius={[3,3,0,0]} />
                                                    <Bar dataKey="atrasadas" name="Atrasadas" fill="#f97316" radius={[3,3,0,0]} />
                                                    <Bar dataKey="devolucoes_recebidas" name="Devoluções" fill="#2563eb" radius={[3,3,0,0]} />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </Box>

                                    <Box bg="white" p={4} rounded="md" shadow="sm" borderWidth="1px" borderColor="gray.100">
                                        <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={3} textTransform="uppercase">
                                            Remuneração por Semana
                                        </Text>
                                        <Box h={{ base: '220px', md: '260px' }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart
                                                    data={[...semanal.semanas].reverse().map(s => ({
                                                        semana: s.semana_label.split(' - ')[0],
                                                        valor_despacho: s.remuneracao.valor_despacho,
                                                        valor_devolucoes: s.remuneracao.valor_devolucoes,
                                                        valor_total: s.remuneracao.valor_total,
                                                    }))}
                                                    barCategoryGap="20%"
                                                >
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                                    <XAxis dataKey="semana" tick={{ fontSize: 10 }} />
                                                    <YAxis tick={{ fontSize: 10 }} width={50} tickFormatter={(v) => `R$${v}`} />
                                                    <RTooltip content={<ChartTooltipSemanal />} />
                                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                                    <Bar dataKey="valor_despacho" name="Despacho" fill="#16a34a" radius={[3,3,0,0]} stackId="rem" />
                                                    <Bar dataKey="valor_devolucoes" name="Devoluções" fill="#2563eb" radius={[3,3,0,0]} stackId="rem" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </Box>
                                    </Box>
                                </SimpleGrid>

                                {chartsDerivedData && (
                                    <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4} mt={4}>
                                        <Box bg="white" p={4} rounded="md" shadow="sm" borderWidth="1px" borderColor="gray.100">
                                            <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={3} textTransform="uppercase">
                                                Evolução Acumulada
                                            </Text>
                                            <Box h={{ base: '200px', md: '220px' }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <AreaChart data={chartsDerivedData.acumuladoData}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                                        <XAxis dataKey="data_formatada" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                                                        <YAxis tick={{ fontSize: 9 }} allowDecimals={false} width={30} />
                                                        <RTooltip content={<ChartTooltipAcumulado />} />
                                                        <Area type="monotone" dataKey="acumulado" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.15} strokeWidth={2} />
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            </Box>
                                        </Box>

                                        <Box bg="white" p={4} rounded="md" shadow="sm" borderWidth="1px" borderColor="gray.100">
                                            <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={3} textTransform="uppercase">
                                                Taxa de Falhas (%)
                                            </Text>
                                            <Box h={{ base: '200px', md: '220px' }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <ComposedChart data={chartsDerivedData.taxaProblemasData}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                                        <XAxis dataKey="data_formatada" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                                                        <YAxis tick={{ fontSize: 9 }} width={30} domain={[0, 'auto']} unit="%" />
                                                        <RTooltip content={<ChartTooltipTaxa />} />
                                                        <Area type="monotone" dataKey="taxa_problemas" fill="#fecaca" stroke="none" fillOpacity={0.5} />
                                                        <Line type="monotone" dataKey="taxa_problemas" stroke="#dc2626" strokeWidth={2} dot={{ r: 3, fill: '#dc2626' }} />
                                                    </ComposedChart>
                                                </ResponsiveContainer>
                                            </Box>
                                        </Box>

                                        <Box bg="white" p={4} rounded="md" shadow="sm" borderWidth="1px" borderColor="gray.100">
                                            <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={3} textTransform="uppercase">
                                                Cumprimento de Prazo (%)
                                            </Text>
                                            <Box h={{ base: '200px', md: '220px' }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <ComposedChart data={chartsDerivedData.taxaPrazoData}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                                        <XAxis dataKey="data_formatada" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                                                        <YAxis tick={{ fontSize: 9 }} width={30} domain={[0, 100]} unit="%" />
                                                        <RTooltip content={<ChartTooltipPrazo />} />
                                                        <Area type="monotone" dataKey="taxa_prazo" fill="#d1fae5" stroke="none" fillOpacity={0.5} />
                                                        <Line type="monotone" dataKey="taxa_prazo" stroke="#16a34a" strokeWidth={2} dot={{ r: 3, fill: '#16a34a' }} />
                                                    </ComposedChart>
                                                </ResponsiveContainer>
                                            </Box>
                                        </Box>
                                    </SimpleGrid>
                                )}
                            </>
                        )}

                        <Box bg="gray.50" p={3} rounded="md" mt={3} borderWidth="1px" borderColor="gray.200">
                            <Text fontSize="10px" color="gray.500" fontWeight="bold" mb={1}>Regras de remuneração (por semana)</Text>
                            <Text fontSize="10px" color="gray.500">Despacho: 1-100 = R$10,00 | 101-150 = R$5,00 | 151+ = R$1,00 por etiqueta</Text>
                            <Text fontSize="10px" color="gray.500">Devoluções: R$10,00 por etiqueta recebida</Text>
                        </Box>
                    </Box>
                )}
            </Container>
        </Box>
    );
}

function CardTotal({ label, valor, cor, icon: Icon }) {
    return (
        <Stat
            bg="white"
            p={{ base: 2, md: 4 }}
            rounded="md"
            shadow="sm"
            borderLeft="4px solid"
            borderColor={`${cor}.400`}
        >
            <Flex justify="space-between" align="flex-start">
                <Box>
                    <StatLabel color="gray.600" fontSize={{ base: '10px', md: 'sm' }}>{label}</StatLabel>
                    <StatNumber color={`${cor}.600`} fontSize={{ base: 'xl', md: '2xl' }}>{valor}</StatNumber>
                </Box>
                <Box as={Icon} color={`${cor}.400`} boxSize={{ base: 4, md: 5 }} mt={1} />
            </Flex>
        </Stat>
    );
}

function LinhaMobile({ label, valor, cor }) {
    return (
        <HStack justify="space-between" bg={`${cor}.50`} px={2} py={1} rounded="sm">
            <Text fontSize="11px" color="gray.600" fontWeight="medium">{label}</Text>
            <Badge colorScheme={cor} fontSize="11px">{valor}</Badge>
        </HStack>
    );
}
